import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { LanguageProvider, useLanguage } from './LanguageProvider';
import { ToastProvider, useToast } from './ToastProvider';
import Services from '../services/Services';

interface LoadingState {
  [key: string]: boolean;
}

interface GeneralContextType {
  // Auth methods
  auth: ReturnType<typeof useAuth>;
  // Language methods
  language: ReturnType<typeof useLanguage>;
  // Services
  services: typeof Services;
  // Loading state management
  loading: LoadingState;
  setLoading: (key: string, value: boolean) => void;
  isLoading: (key: string) => boolean;
  clearLoading: (key: string) => void;
  clearAllLoading: () => void;
  // Helper methods
  toastSuccess: (message: string) => void;
  toastError: (message: string) => void;
}

const GeneralContext = createContext<GeneralContextType | undefined>(undefined);

interface GeneralProviderProps {
  children: ReactNode;
}

// Inner component that has access to both Auth and Language contexts
const GeneralProviderInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const language = useLanguage();
  const { showToast } = useToast();
  const [loading, setLoadingState] = useState<LoadingState>({});

  // Loading state management functions
  const setLoading = useCallback((key: string, value: boolean) => {
    setLoadingState((prev) => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const isLoading = useCallback((key: string): boolean => {
    return loading[key] === true;
  }, [loading]);

  const clearLoading = useCallback((key: string) => {
    setLoadingState((prev) => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  }, []);

  const clearAllLoading = useCallback(() => {
    setLoadingState({});
  }, []);

  // Toast implementation using ToastProvider
  const toastSuccess = useCallback(
    (message: string) => {
      showToast(message, 'success', 3000);
    },
    [showToast]
  );

  const toastError = useCallback(
    (message: string) => {
      showToast(message, 'error', 4000);
    },
    [showToast]
  );

  const value: GeneralContextType = {
    auth,
    language,
    services: Services,
    loading,
    setLoading,
    isLoading,
    clearLoading,
    clearAllLoading,
    toastSuccess,
    toastError
  };

  return (
    <GeneralContext.Provider value={value}>
      {children}
    </GeneralContext.Provider>
  );
};

// Main provider that wraps everything
export const GeneralProvider: React.FC<GeneralProviderProps> = ({ children }) => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ToastProvider>
          <GeneralProviderInner>
            {children}
          </GeneralProviderInner>
        </ToastProvider>
      </AuthProvider>
    </LanguageProvider>
  );
};

export const useGeneral = (): GeneralContextType => {
  const context = useContext(GeneralContext);
  if (context === undefined) {
    throw new Error('useGeneral must be used within a GeneralProvider');
  }
  return context;
};

// Convenience hooks that use GeneralProvider
export const useAuthFromGeneral = () => {
  const { auth } = useGeneral();
  return auth;
};

export const useLanguageFromGeneral = () => {
  const { language } = useGeneral();
  return language;
};

export const useServicesFromGeneral = () => {
  const { services } = useGeneral();
  return services;
};

